import { useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Copy, Download, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { CLINIC } from "@/constants/testIds";

/** Card de Ajustes: URL/slug público, redes sociales y QR descargable de /reservas/:slug. */
export default function PublicBookingSettingsCard({ clinic, onUpdated }) {
  const [slugInput, setSlugInput] = useState(clinic.slug || "");
  const [instagram, setInstagram] = useState(clinic.instagram_url || "");
  const [facebook, setFacebook] = useState(clinic.facebook_url || "");
  const [saving, setSaving] = useState(false);
  const qrWrapperRef = useRef(null);
  const { toast } = useToast();

  const publicUrl = `${window.location.origin}/reservas/${clinic.slug || ""}`;

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/clinic", {
        slug: slugInput,
        instagram_url: instagram || null,
        facebook_url: facebook || null,
      });
      onUpdated(data);
      setSlugInput(data.slug || "");
      toast({ title: "Página pública actualizada" });
    } catch (err) {
      toast({ title: "Error al guardar", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(publicUrl);
    toast({ title: "Enlace copiado" });
  };

  const handleDownloadQr = () => {
    const canvas = qrWrapperRef.current?.querySelector("canvas");
    if (!canvas) return;
    const link = document.createElement("a");
    link.href = canvas.toDataURL("image/png");
    link.download = `qr-reservas-${clinic.slug || "clínica"}.png`;
    link.click();
  };

  return (
    <div className="bg-white border border-border rounded-xl p-6 sm:p-8 shadow-sm space-y-5">
      <div>
        <h2 className="font-serif text-xl text-foreground">Página pública de reservas</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Tus pacientes pueden reservar turnos 24/7 desde este enlace, sin necesidad de iniciar sesión.
        </p>
      </div>

      <div>
        <Label className="text-sm text-muted-foreground mb-1 block">URL personalizada (slug)</Label>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground shrink-0 hidden sm:inline">{window.location.origin}/reservas/</span>
          <Input value={slugInput} onChange={(e) => setSlugInput(e.target.value)} placeholder="mi-clínica" data-testid={CLINIC.slugInput} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-sm text-muted-foreground mb-1 block">Instagram</Label>
          <Input value={instagram} onChange={(e) => setInstagram(e.target.value)} placeholder="https://instagram.com/tuclinica" data-testid={CLINIC.instagramInput} />
        </div>
        <div>
          <Label className="text-sm text-muted-foreground mb-1 block">Facebook</Label>
          <Input value={facebook} onChange={(e) => setFacebook(e.target.value)} placeholder="https://facebook.com/tuclinica" data-testid={CLINIC.facebookInput} />
        </div>
      </div>

      <div className="pt-1">
        <Button onClick={handleSave} disabled={saving} className="bg-brand hover:bg-brand-hover text-white" data-testid={CLINIC.publicBookingSaveButton}>
          {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
          Guardar cambios
        </Button>
      </div>

      {clinic.slug && (
        <div className="border-t border-border pt-5 flex flex-col sm:flex-row items-center gap-5">
          <div ref={qrWrapperRef} className="p-3 bg-white border border-border rounded-xl shrink-0" data-testid={CLINIC.qrCanvas}>
            <QRCodeCanvas value={publicUrl} size={128} />
          </div>
          <div className="flex-1 text-center sm:text-left w-full">
            <p className="text-sm text-foreground font-medium">Código QR para folleteria y redes</p>
            <div className="flex items-center gap-2 mt-2">
              <Input readOnly value={publicUrl} className="text-sm" data-testid={CLINIC.publicUrlPreview} />
              <Button type="button" variant="outline" size="icon" onClick={handleCopy} data-testid={CLINIC.copyPublicUrlButton}>
                <Copy className="w-4 h-4" />
              </Button>
            </div>
            <Button type="button" variant="outline" size="sm" className="mt-3" onClick={handleDownloadQr} data-testid={CLINIC.qrDownloadButton}>
              <Download className="w-4 h-4 mr-2" /> Descargar QR
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
